import React, { useState, useRef } from 'react';
import { Box, TextField, IconButton, Button, InputAdornment, Paper, CircularProgress, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import AddIcon from '@mui/icons-material/Add';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useAppDispatch, analyzeProperty, analyzeAllTools, setProgressModalOpen } from '../store';
import { AnalysisToolType } from '../types/property';
import { useSnackbar } from 'notistack';

interface MainInputProps {
  onSubmit: (value: string) => void;
  loading?: boolean;
}

const TRADING_PROMPT = "What are the support and resistance levels on this technical chart based on price? Please identify the asset name (stock symbol, crypto, ETF, etc.) and provide specific price levels. What are the fibonacci levels that can be inferred based on the swing high and swing low price levels? What are the fibonacci extensions levels and market context";

const MainInput: React.FC<MainInputProps> = ({ onSubmit, loading = false }) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const [value, setValue] = useState('');
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [realEstateMenuAnchor, setRealEstateMenuAnchor] = useState<null | HTMLElement>(null);
  const realEstateMenuOpen = Boolean(realEstateMenuAnchor);
  const [zillowMenuAnchor, setZillowMenuAnchor] = useState<null | HTMLElement>(null);
  const [redfinMenuAnchor, setRedfinMenuAnchor] = useState<null | HTMLElement>(null);
  const [zillowUrl, setZillowUrl] = useState('');
  const [redfinUrl, setRedfinUrl] = useState('');
  const zillowMenuOpen = Boolean(zillowMenuAnchor);
  const redfinMenuOpen = Boolean(redfinMenuAnchor);
  const [zillowError, setZillowError] = useState('');
  const [redfinError, setRedfinError] = useState('');
  const [zillowPopoverAnchor, setZillowPopoverAnchor] = useState<null | HTMLElement>(null);
  const [redfinPopoverAnchor, setRedfinPopoverAnchor] = useState<null | HTMLElement>(null);
  const zillowPopoverOpen = Boolean(zillowPopoverAnchor);
  const redfinPopoverOpen = Boolean(redfinPopoverAnchor);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [toolsDropdownAnchor, setToolsDropdownAnchor] = useState<null | HTMLElement>(null);
  const handleToolsButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setToolsDropdownAnchor(event.currentTarget);
    setToolsDropdownOpen((prev) => !prev);
  };
  const handleToolsDropdownClose = () => {
    setToolsDropdownOpen(false);
    setToolsDropdownAnchor(null);
  };

  const handleTradingSelect = () => {
    onSubmit(TRADING_PROMPT);
    handleToolsDropdownClose();
  };

  const handleToolClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleRealEstateMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRealEstateMenuAnchor(event.currentTarget);
  };
  const handleRealEstateMenuClose = () => {
    setRealEstateMenuAnchor(null);
  };
  const handleZillowMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setZillowMenuAnchor(event.currentTarget);
  };
  const handleZillowMenuClose = () => {
    setZillowMenuAnchor(null);
  };
  const handleRedfinMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRedfinMenuAnchor(event.currentTarget);
  };
  const handleRedfinMenuClose = () => {
    setRedfinMenuAnchor(null);
  };

  const handleZillowPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setZillowPopoverAnchor(event.currentTarget);
    // Do NOT close the parent Menu here
  };
  const handleZillowPopoverClose = () => {
    setZillowPopoverAnchor(null);
    handleMenuClose(); // Close the parent Menu when Popover closes
  };
  const handleRedfinPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRedfinPopoverAnchor(event.currentTarget);
    // Do NOT close the parent Menu here
  };
  const handleRedfinPopoverClose = () => {
    setRedfinPopoverAnchor(null);
    handleMenuClose(); // Close the parent Menu when Popover closes
  };

  const handleZillowToolSelect = async (tool: string) => {
    if (!zillowUrl.trim()) {
      setZillowError('Please enter a Zillow URL');
      return;
    }
    setZillowError('');
    setZillowMenuAnchor(null);

    try {
      let selectedTools: AnalysisToolType[] = [];

      if (tool === 'Analyze All') {
        // Trigger analyze all tools
        enqueueSnackbar('Starting comprehensive property analysis...', { variant: 'info' });
        dispatch(setProgressModalOpen(true));
        await dispatch(analyzeAllTools({
          url: zillowUrl.trim(),
          source: 'zillow' as const,
        })).unwrap();
      } else {
        // Map tool names to AnalysisToolType
        const toolMapping: Record<string, AnalysisToolType> = {
          'Property Data Parser': 'propertyParser',
          'Floor Plan Analysis': 'floorPlan',
          'Comparative Market Analysis (CMA)': 'cma',
          'Neighborhood Intelligence': 'neighborhood',
          'Pricing Strategy': 'pricing',
          'Negotiation Support': 'negotiation',
          'Listing Description Generator': 'listingDescription',
          'Client Consultation Prep': 'clientConsultation',
          'Lead Qualification': 'leadQualification',
          'Transaction Management': 'transactionManagement',
          'Pre-Tour Prep Cascade': 'preTourPrep',
          'Client Match': 'clientMatch',
          'AI Buyer Sentiment Simulator': 'buyerSentiment',
          'Agent Orchestrator (Meta-Agent)': 'agentOrchestrator',
        };

        const mappedTool = toolMapping[tool];
        if (!mappedTool) {
          enqueueSnackbar(`Tool "${tool}" is not yet available`, { variant: 'warning' });
          return;
        }

        selectedTools = [mappedTool];
        enqueueSnackbar(`Starting ${tool} analysis...`, { variant: 'info' });
        
        await dispatch(analyzeProperty({
          url: zillowUrl.trim(),
          source: 'zillow' as const,
          selectedTools,
        })).unwrap();
      }

      enqueueSnackbar('Property analysis started successfully!', { variant: 'success' });
      setZillowUrl(''); // Clear the URL after successful submission
      
    } catch (error: any) {
      console.error('Property analysis failed:', error);
      enqueueSnackbar(
        error.message || 'Failed to start property analysis',
        { variant: 'error' }
      );
    }
  };
  const handleRedfinToolSelect = async (tool: string) => {
    if (!redfinUrl.trim()) {
      setRedfinError('Please enter a Redfin URL');
      return;
    }

    // Validate Redfin URL format
    const redfinPattern = /^https?:\/\/(www\.)?redfin\.com\/.*\/home\/\d+$/;
    if (!redfinPattern.test(redfinUrl.trim())) {
      setRedfinError('Please enter a valid Redfin property URL');
      return;
    }

    setRedfinError('');
    setRedfinMenuAnchor(null);

    try {
      let selectedTools: AnalysisToolType[] = [];

      if (tool === 'Analyze All') {
        // Trigger analyze all tools
        enqueueSnackbar('Starting comprehensive property analysis...', { variant: 'info' });
        dispatch(setProgressModalOpen(true));
        await dispatch(analyzeAllTools({
          url: redfinUrl.trim(),
          source: 'redfin' as const,
        })).unwrap();
      } else {
        // Map tool names to AnalysisToolType
        const toolMapping: Record<string, AnalysisToolType> = {
          'Property Data Parser': 'propertyParser',
          'Floor Plan Analysis': 'floorPlan',
          'Comparative Market Analysis (CMA)': 'cma',
          'Neighborhood Intelligence': 'neighborhood',
          'Pricing Strategy': 'pricing',
          'Negotiation Support': 'negotiation',
          'Listing Description Generator': 'listingDescription',
          'Client Consultation Prep': 'clientConsultation',
          'Lead Qualification': 'leadQualification',
          'Transaction Management': 'transactionManagement',
          'Pre-Tour Prep Cascade': 'preTourPrep',
          'Client Match': 'clientMatch',
          'AI Buyer Sentiment Simulator': 'buyerSentiment',
          'Agent Orchestrator (Meta-Agent)': 'agentOrchestrator',
        };

        const mappedTool = toolMapping[tool];
        if (!mappedTool) {
          enqueueSnackbar(`Tool "${tool}" is not yet available`, { variant: 'warning' });
          return;
        }

        selectedTools = [mappedTool];
        enqueueSnackbar(`Starting ${tool} analysis...`, { variant: 'info' });
        
        await dispatch(analyzeProperty({
          url: redfinUrl.trim(),
          source: 'redfin' as const,
          selectedTools,
        })).unwrap();
      }

      enqueueSnackbar('Property analysis started successfully!', { variant: 'success' });
      setRedfinUrl(''); // Clear the URL after successful submission
      
    } catch (error: any) {
      console.error('Property analysis failed:', error);
      enqueueSnackbar(
        error.message || 'Failed to start property analysis',
        { variant: 'error' }
      );
    }
  };

  // Check for Web Speech API support
  const isSpeechSupported = typeof window !== 'undefined' && ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value);
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (value.trim()) {
      onSubmit(value);
      setValue('');
    }
  };

  const handleVoiceInput = () => {
    if (!isSpeechSupported) {
      alert('Voice input is not supported in this browser.');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; ++i) {
        transcript += event.results[i][0].transcript;
      }
      setValue(transcript);
    };
    recognition.onend = () => {
      setListening(false);
    };
    recognition.onerror = () => {
      setListening(false);
    };
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  const [realEstateSubmenuOpen, setRealEstateSubmenuOpen] = useState(false);
  const [realEstateSubmenuAnchor, setRealEstateSubmenuAnchor] = useState<null | HTMLElement>(null);
  const [zillowSubmenuOpen, setZillowSubmenuOpen] = useState(false);
  const [zillowSubmenuAnchor, setZillowSubmenuAnchor] = useState<null | HTMLElement>(null);
  const [redfinSubmenuOpen, setRedfinSubmenuOpen] = useState(false);
  const [redfinSubmenuAnchor, setRedfinSubmenuAnchor] = useState<null | HTMLElement>(null);

  // Handlers for opening/closing submenus
  const handleRealEstateSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRealEstateSubmenuAnchor(event.currentTarget);
    setRealEstateSubmenuOpen(true);
    setZillowSubmenuOpen(false);
    setRedfinSubmenuOpen(false);
  };
  const handleRealEstateSubmenuClose = () => {
    setRealEstateSubmenuOpen(false);
    setRealEstateSubmenuAnchor(null);
    setZillowSubmenuOpen(false);
    setRedfinSubmenuOpen(false);
  };
  const handleZillowSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setZillowSubmenuAnchor(event.currentTarget);
    setZillowSubmenuOpen(true);
    setRedfinSubmenuOpen(false);
  };
  const handleZillowSubmenuClose = () => {
    setZillowSubmenuOpen(false);
    setZillowSubmenuAnchor(null);
  };
  const handleRedfinSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRedfinSubmenuAnchor(event.currentTarget);
    setRedfinSubmenuOpen(true);
    setZillowSubmenuOpen(false);
  };
  const handleRedfinSubmenuClose = () => {
    setRedfinSubmenuOpen(false);
    setRedfinSubmenuAnchor(null);
  };

  return (
    <Paper elevation={4} sx={{ p: 1, borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.18)' : 'transparent', display: 'flex', alignItems: 'center', mt: 10, mb: 2, maxWidth: 800, width: '100%', mx: 'auto', backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined, WebkitBackdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined, border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : undefined }} role="form" aria-label="Ask AI about your screen">
      <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <IconButton onClick={handleToolsButtonClick} aria-label="Open tools menu">
            <AddIcon />
          </IconButton>
          {toolsDropdownOpen && (
            <Paper
              elevation={8}
              sx={{ position: 'absolute', zIndex: 99999, minWidth: 220, mt: 1, left: 0, isolation: 'isolate' }}
              onMouseLeave={handleToolsDropdownClose}
            >
              <List disablePadding>
                <ListItem disablePadding onMouseEnter={handleRealEstateSubmenuOpen} onMouseLeave={handleRealEstateSubmenuClose}>
                  <ListItemButton>
                    <ListItemText primary="Real Estate" />
                    <ArrowRightIcon fontSize="small" />
                  </ListItemButton>
                  {realEstateSubmenuOpen && realEstateSubmenuAnchor && (
                    <Paper
                      elevation={8}
                      sx={{ position: 'absolute', left: '100%', top: 0, minWidth: 180, zIndex: 100000, isolation: 'isolate' }}
                      onMouseLeave={handleRealEstateSubmenuClose}
                      onMouseEnter={handleRealEstateSubmenuOpen}
                    >
                      <List disablePadding>
                        <ListItem disablePadding onMouseEnter={handleZillowSubmenuOpen} onMouseLeave={handleZillowSubmenuClose}>
                          <ListItemButton>
                            <ListItemText primary="Zillow" />
                            <ArrowRightIcon fontSize="small" />
                          </ListItemButton>
                          {zillowSubmenuOpen && zillowSubmenuAnchor && (
                            <Paper
                              elevation={8}
                              sx={{ position: 'absolute', left: '100%', top: 0, minWidth: 280, zIndex: 100001, p: 2, isolation: 'isolate' }}
                              onMouseLeave={handleZillowSubmenuClose}
                              onMouseEnter={handleZillowSubmenuOpen}
                            >
                              <Box sx={{ mb: 2 }}>
                                <TextField
                                  size="small"
                                  label="Paste Zillow URL"
                                  value={zillowUrl}
                                  onChange={e => { setZillowUrl(e.target.value); setZillowError(''); }}
                                  variant="outlined"
                                  fullWidth
                                  error={!!zillowError}
                                  helperText={zillowError}
                                />
                              </Box>
                              <List disablePadding>
                                <ListItem disablePadding><ListItemButton onClick={() => handleZillowToolSelect('Analyze All')} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 'bold', mb: 1 }}>🚀 Analyze All</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Property Data Parser')}>Property Data Parser</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Floor Plan Analysis')}>Floor Plan Analysis</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Comparative Market Analysis (CMA)')}>Comparative Market Analysis (CMA)</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Neighborhood Intelligence')}>Neighborhood Intelligence</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Pricing Strategy')}>Pricing Strategy</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Negotiation Support')}>Negotiation Support</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Listing Description Generator')}>Listing Description Generator</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Client Consultation Prep')}>Client Consultation Prep</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Lead Qualification')}>Lead Qualification</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Transaction Management')}>Transaction Management</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Pre-Tour Prep Cascade')}>Pre-Tour Prep Cascade</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Client Match')}>Client Match</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('AI Buyer Sentiment Simulator')}>AI Buyer Sentiment Simulator</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleZillowToolSelect('Agent Orchestrator (Meta-Agent)')}>Agent Orchestrator (Meta-Agent)</ListItemButton></ListItem>
                              </List>
                            </Paper>
                          )}
                        </ListItem>
                        <ListItem disablePadding onMouseEnter={handleRedfinSubmenuOpen} onMouseLeave={handleRedfinSubmenuClose}>
                          <ListItemButton>
                            <ListItemText primary="Redfin" />
                            <ArrowRightIcon fontSize="small" />
                          </ListItemButton>
                          {redfinSubmenuOpen && redfinSubmenuAnchor && (
                            <Paper
                              elevation={8}
                              sx={{ position: 'absolute', left: '100%', top: 0, minWidth: 280, zIndex: 100001, p: 2, isolation: 'isolate' }}
                              onMouseLeave={handleRedfinSubmenuClose}
                              onMouseEnter={handleRedfinSubmenuOpen}
                            >
                              <Box sx={{ mb: 2 }}>
                                <TextField
                                  size="small"
                                  label="Paste Redfin URL"
                                  value={redfinUrl}
                                  onChange={e => { setRedfinUrl(e.target.value); setRedfinError(''); }}
                                  variant="outlined"
                                  fullWidth
                                  error={!!redfinError}
                                  helperText={redfinError}
                                />
                              </Box>
                              <List disablePadding>
                                <ListItem disablePadding><ListItemButton onClick={() => handleRedfinToolSelect('Analyze All')} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 'bold', mb: 1 }}>🚀 Analyze All</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Property Data Parser')}>Property Data Parser</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Floor Plan Analysis')}>Floor Plan Analysis</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Comparative Market Analysis (CMA)')}>Comparative Market Analysis (CMA)</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Neighborhood Intelligence')}>Neighborhood Intelligence</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Pricing Strategy')}>Pricing Strategy</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Negotiation Support')}>Negotiation Support</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Listing Description Generator')}>Listing Description Generator</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Client Consultation Prep')}>Client Consultation Prep</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Lead Qualification')}>Lead Qualification</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Transaction Management')}>Transaction Management</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Pre-Tour Prep Cascade')}>Pre-Tour Prep Cascade</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Client Match')}>Client Match</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('AI Buyer Sentiment Simulator')}>AI Buyer Sentiment Simulator</ListItemButton></ListItem>
                                <ListItem disablePadding><ListItemButton disabled onClick={() => handleRedfinToolSelect('Agent Orchestrator (Meta-Agent)')}>Agent Orchestrator (Meta-Agent)</ListItemButton></ListItem>
                              </List>
                            </Paper>
                          )}
                        </ListItem>
                      </List>
                    </Paper>
                  )}
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Auto Insurance" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Home Insurance" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Medical Tools" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Dental Tools" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleTradingSelect}>
                    <ListItemText primary="Trading" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Add Connectors" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary="Manage Connectors" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          )}
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ask anything about what you see"
          value={value}
          onChange={handleInputChange}
          InputProps={{
            sx: { 
              borderRadius: 2, 
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.18)' : 'transparent',
              fontSize: 16, 
              py: 0.5, 
              textAlign: 'center',
              backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined,
              WebkitBackdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(20px)' : undefined,
              border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : undefined
            },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={isSpeechSupported ? (listening ? 'Stop voice input' : 'Start voice input') : 'Voice input not supported'}>
                  <span>
                    <IconButton
                      aria-label="voice input"
                      onClick={handleVoiceInput}
                      edge="end"
                      tabIndex={0}
                      color={listening ? 'primary' : 'default'}
                      disabled={!isSpeechSupported || loading}
                    >
                      {listening ? <MicIcon /> : <MicOffIcon />}
                      {listening && <span style={{ color: '#4facfe', marginLeft: 4, fontSize: 18 }}>●</span>}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Ask about your screen', style: { fontSize: 16, padding: '8px 0', textAlign: 'center' } }}
        />
      </form>
    </Paper>
  );
};

export default MainInput; 